import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useFunnelNavigation from '@/hooks/useFunnelNavigation';
import Logo from '@/components/Logo';

const variantStyles = {
  28: 'rounded-xl bg-[#EFEFEF] text-[#333333]',
  default: 'rounded-full bg-[#EBEBEB] text-black',
};

const Navbar = ({
  showNavigation = true,
  showSignIn = false,
  showGetStarted = true,
  customNavItems,
  getStartedUrl = null,
  logoHasRedirect = false,
}) => {
  const { navigateToCheckout, navigateTo } = useFunnelNavigation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { funnelId } = useParams();
  const buttonStyles = variantStyles[funnelId] || variantStyles.default;

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  // Default navigation items
  const defaultNavItems = [
    { label: 'How it Works', href: '#how-it-works' },
    { label: 'Features', href: '#features' },
    { label: 'Success Stories', href: '#testimonials' },
    { label: 'Why it Works', href: '#why-works' },
  ];

  const navItems = customNavItems || defaultNavItems;

  return (
    <nav className="bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <Logo logoHasRedirect={logoHasRedirect} />
          </div>

          {/* Desktop Navigation */}
          {showNavigation && (
            <div className="hidden md:flex space-x-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="text-black font-['Inter'] text-sm font-normal leading-5 transition-colors cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
            </div>
          )}

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {showSignIn && (
              <Link
                to="/login"
                className={`font-['Inter'] text-sm font-semibold leading-5 px-5 py-2.5 hover:bg-gray-200 ${buttonStyles}`}
              >
                Sign in
              </Link>
            )}
            {showGetStarted && (
              <button
                onClick={
                  getStartedUrl
                    ? () => navigateTo(getStartedUrl, {}, {}, 6)
                    : () => navigateToCheckout(null, { funnelId: 28 })
                }
                className={`font-['Inter'] text-sm font-semibold leading-5 px-5 py-2.5 hover:bg-gray-200 ${buttonStyles}`}
              >
                Get Started
              </button>
            )}
          </div>

          {/* Mobile Buttons and Menu */}
          <div className="md:hidden flex items-center gap-3">
            {showSignIn && (
              <Link
                to="/login"
                className={`font-['Inter'] text-sm font-semibold leading-5 px-5 py-2.5 hover:bg-gray-200 ${buttonStyles}`}
              >
                Sign in
              </Link>
            )}
            {showGetStarted && (
              <button
                onClick={
                  getStartedUrl
                    ? () => navigateTo(getStartedUrl, {}, {}, 6)
                    : () => navigateToCheckout(null, { funnelId: 28 })
                }
                className={`font-['Inter'] text-sm font-semibold leading-5 px-5 py-2.5 hover:bg-gray-200 ${buttonStyles}`}
              >
                Get Started
              </button>
            )}
            {showNavigation && (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
                className="inline-flex items-center justify-center p-2 text-gray-600 outline-none"
              >
                <span className="sr-only">Open main menu</span>
                {!isMenuOpen ? (
                  <svg
                    className="block h-6 w-6 transition-all duration-500 ease-in-out"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg
                    className="block h-6 w-6 transition-all duration-500 ease-in-out rotate-90"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {showNavigation && (
        <div
          className={`absolute top-16 left-0 right-0 bg-white shadow-lg md:hidden z-50 overflow-hidden transition-all duration-200 ease-in-out ${
            isMenuOpen ? 'opacity-100 translate-y-0' : 'invisible opacity-0 -translate-y-2 pointer-events-none'
          }`}
        >
          <div className="px-2 pt-2 pb-4 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="block px-3 py-2 rounded-md text-black font-['Inter'] text-sm font-normal leading-5 cursor-pointer"
              >
                {item.label}
              </a>
            ))}
            {showSignIn && (
              <div className="mt-4 space-y-2">
                <Link
                  to="/login"
                  className={`block w-full text-center bg-[rgba(0,0,0,0.08)] text-black font-['Inter'] text-sm font-semibold leading-5 px-3 py-2 ${buttonStyles} hover:bg-gray-200`}
                >
                  Sign in
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
